import { useAppSelector } from '@web/redux/store';
import Form from 'carbon-react/lib/components/form';
import Message from 'carbon-react/lib/components/message';
import { useFormik } from 'formik';
import { default as I18n, default as I18njs } from 'i18n-js';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import PublicButton from '../../components/Shared/Public/PublicButton';
import PublicForm from '../../components/Shared/Public/PublicForm';
import PublicPassword from '../../components/Shared/Public/PublicPassword';
import PublicTextBox from '../../components/Shared/Public/PublicTextBox';
import Translate from '../../components/Translate/Translate';

const Actions = styled.div`
  display: flex;
  gap: 20px;
`;

export interface RegisterFormValues {
  companyId: string;
  username: string;
  password: string;
  passwordConfirm: string;
}

export interface RegisterFormProps {
  initialValues?: RegisterFormValues;
  onSubmit: (values: RegisterFormValues) => void;
  errorMsg?: string;
  passwordOnly?: boolean;
}

const INITIAL_VALUES: RegisterFormValues = {
  companyId: '',
  username: '',
  password: '',
  passwordConfirm: '',
};

const validate = (values: RegisterFormValues) => {
  const errors: Partial<RegisterFormValues> = {};

  if (!values.companyId) {
    errors.companyId = I18n.t('common.Required');
  }

  if (!values.username) {
    errors.username = I18n.t('common.Required');
  }

  if (!values.password) {
    errors.password = I18n.t('common.Required');
  } else if (values.password.length < 8) {
    errors.password = I18n.t('auth.MustBe', { number: 8 });
  }

  if (!values.passwordConfirm) {
    errors.passwordConfirm = I18n.t('common.Required');
  } else if (values.password !== values.passwordConfirm) {
    errors.passwordConfirm = I18njs.t('auth.PasswordsDontMatch');
  }

  return errors;
};

const InviteForm = ({ errorMsg, onSubmit, initialValues = INITIAL_VALUES, passwordOnly = false }: RegisterFormProps) => {
  const formSubmitting = useAppSelector((state) => state.user.loading);

  const { handleSubmit, values, errors, handleChange } = useFormik<RegisterFormValues>({
    initialValues,
    validate,
    isInitialValid: false,
    validateOnChange: false,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  const { password, passwordConfirm, username, companyId } = values;

  return (
    <PublicForm
      title="auth.CreateAccount"
      subTitle="auth.WevepulledyourCompanyIDandUserIDfromIntacct"
      footerComponent={
        <Actions>
          <Translate scope="auth.Alreadyhaveaccount" />
          <Link to="/login">
            <Translate scope="auth.LogIn" />
          </Link>
        </Actions>
      }
    >
      <Form
        saveButton={<PublicButton buttonType="primary" key="register" type="submit" label="auth.CreateAccount" />}
        onSubmit={handleSubmit}
      >
        <PublicTextBox
          readOnly={passwordOnly}
          label={I18njs.t('auth.CompanyID')}
          labelInline={false}
          labelAlign="left"
          name="companyId"
          value={companyId}
          onChange={handleChange}
          error={errors.companyId}
        />
        <PublicTextBox
          readOnly={passwordOnly}
          label={I18njs.t('auth.UserID')}
          labelInline={false}
          labelAlign="left"
          name="username"
          value={username}
          onChange={handleChange}
          error={errors.username}
        />
        <PublicPassword
          label={I18njs.t('auth.Password')}
          forceObscurity={formSubmitting}
          labelInline={false}
          labelAlign="left"
          name="password"
          onChange={handleChange}
          value={password}
          error={errors.password}
        />
        <PublicPassword
          label={I18njs.t('auth.ConfirmPassword')}
          forceObscurity={formSubmitting}
          labelInline={false}
          labelAlign="left"
          name="passwordConfirm"
          onChange={handleChange}
          value={passwordConfirm}
          error={errors.passwordConfirm}
        />
        {errorMsg && (
          <div className="error-wrapper">
            <Message variant="error">{errorMsg}</Message>
          </div>
        )}
      </Form>
    </PublicForm>
  );
};

export default InviteForm;
